import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { CassetteReferencePosition, SynopticService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

export class CreateCassetteForm extends FormGroup<{
    cassette: FormControl<CassetteReferencePosition>;
}> {}

export type CreateCassetteModalData = {
    depositId: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ComplexModalTemplate, RessourcesModule, SelectComponent],
    selector: 'app-create-cassette-modal',
    templateUrl: './create-cassette-modal.component.html',
})
export class CreateCassetteModalComponent implements OnInit {
    public readonly cassetteOptions = signal<SelectOption<CassetteReferencePosition>[]>([]);

    protected createCassetteForm!: CreateCassetteForm;
    protected data: CreateCassetteModalData = inject(MAT_DIALOG_DATA);
    protected dialogRef = inject(MatDialogRef<CreateCassetteModalComponent>);

    private readonly synopticService = inject(SynopticService);

    public async ngOnInit(): Promise<void> {
        this.initForm();
        await this.initCassetteOptions();
    }

    public onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close(null);

            return;
        }

        this.dialogRef.close(this.createCassetteForm.getRawValue().cassette);
    }

    public onSelectedCassetteChange(selectedCassette: CassetteReferencePosition[]): void {
        this.createCassetteForm.controls.cassette.setValue(selectedCassette[0]);
        this.createCassetteForm.controls.cassette.markAsDirty();
        this.createCassetteForm.controls.cassette.markAsTouched();
    }

    private async initCassetteOptions(): Promise<void> {
        try {
            const cassettesReferences: CassetteReferencePosition[] = await firstValueFrom(
                this.synopticService.synopticControllerGetCassettesReferences({
                    depositId: this.data.depositId,
                }),
            );

            this.cassetteOptions.set(
                cassettesReferences.map(
                    (cassettesReference: CassetteReferencePosition): SelectOption<CassetteReferencePosition> => {
                        return {
                            label: cassettesReference.rf_nom,
                            value: cassettesReference,
                        };
                    },
                ),
            );
        } catch {
            this.cassetteOptions.set([]);
        }
    }

    private initForm(): void {
        this.createCassetteForm = new CreateCassetteForm({
            cassette: new FormControl<CassetteReferencePosition>(
                {
                    nb_position: null,
                    rf_code: '',
                    rf_nom: '',
                },
                {
                    nonNullable: true,
                    validators: [Validators.required],
                },
            ),
        });
    }
}
